import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/i18n";
import { Loader2 } from "lucide-react";
import Index from "./pages/Index";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";

// Lazy-loaded routes
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Products = lazy(() => import("./pages/Products"));
const History = lazy(() => import("./pages/History"));
const Categories = lazy(() => import("./pages/Categories"));
const Credits = lazy(() => import("./pages/Credits"));
const Settings = lazy(() => import("./pages/Settings"));
const StoreSettings = lazy(() => import("./pages/StoreSettings"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const SEOSettings = lazy(() => import("./pages/SEOSettings"));
const Billing = lazy(() => import("./pages/Billing"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SEOHealth = lazy(() => import("./pages/SEOHealth"));
const KeywordResearch = lazy(() => import("./pages/KeywordResearch"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminAIUsage = lazy(() => import("./pages/admin/AdminAIUsage"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminCompatibility = lazy(() => import("./pages/admin/AdminCompatibility"));
const AdminPluginVersions = lazy(() => import("./pages/admin/AdminPluginVersions"));
const AdminAIPrompts = lazy(() => import("./pages/admin/AdminAIPrompts"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminTickets = lazy(() => import("./pages/admin/AdminTickets"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));

// User pages
const Affiliates = lazy(() => import("./pages/Affiliates"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Support = lazy(() => import("./pages/Support"));

// SEO Pages
const SEOWooCommerce = lazy(() => import("./pages/seo/SEOWooCommerce"));
const DescripcionesProductosSEO = lazy(() => import("./pages/seo/DescripcionesProductosSEO"));
const AlternativaYoast = lazy(() => import("./pages/seo/AlternativaYoast"));
const RankMathVsRankaro = lazy(() => import("./pages/seo/RankMathVsRankaro"));
const PluginSEOWooCommerce = lazy(() => import("./pages/seo/PluginSEOWooCommerce"));

// Static pages
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const IntegrationGuide = lazy(() => import("./pages/IntegrationGuide"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <CookieConsent />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                 
                {/* SEO Pillar Pages */}
                <Route path="/seo-woocommerce" element={<SEOWooCommerce />} />
                <Route path="/descripciones-productos-seo" element={<DescripcionesProductosSEO />} />
                <Route path="/alternativa-yoast" element={<AlternativaYoast />} />
                <Route path="/rank-math-vs-rankaro" element={<RankMathVsRankaro />} />
                <Route path="/plugin-seo-woocommerce" element={<PluginSEOWooCommerce />} />

                {/* Static Pages */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                
                {/* Protected Routes */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/seo-health" element={<SEOHealth />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/products" element={<Products />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/keyword-research" element={<KeywordResearch />} />
                <Route path="/history" element={<History />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/stores" element={<StoreSettings />} />
                <Route path="/settings/api-keys" element={<ApiKeys />} />
                <Route path="/settings/seo" element={<SEOSettings />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/affiliates" element={<Affiliates />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/support" element={<Support />} />
                <Route path="/integration-guide" element={<IntegrationGuide />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/usuarios" element={<AdminUsers />} />
                <Route path="/admin/planes" element={<AdminPlans />} />
                <Route path="/admin/uso-ia" element={<AdminAIUsage />} />
                <Route path="/admin/ai-prompts" element={<AdminAIPrompts />} />
                <Route path="/admin/logs" element={<AdminLogs />} />
                <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                <Route path="/admin/compatibility" element={<AdminCompatibility />} />
                <Route path="/admin/plugin-versions" element={<AdminPluginVersions />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                <Route path="/admin/tickets" element={<AdminTickets />} />
                <Route path="/admin/marketing" element={<AdminMarketing />} />
                
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
